import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, CardBody, Typography, Button, Input, Select, Option, Tooltip } from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faFileInvoice, faSyncAlt, faFilter, faTimes, faChevronLeft, faChevronRight, faTag } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import { formatMoney } from './formatMoney';

const formatDate = (date) => {
    if (!date) return '-';
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime())
        ? '-'
        : parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getFirstAddedDate = (car) => formatDate(car?.input_date || car?.created_at || car?.date_created);

const hasCarDiscount = (car) => Number(car?.discount_amount || 0) > 0 || Boolean(car?.is_discounted);

const OwnerCars = () => {
    const { code } = useParams();
    const navigate = useNavigate();
    const { token, user } = useAuth();
    const authToken = token || localStorage.getItem('authToken');

    const [owner, setOwner] = useState(null);
    const [cars, setCars] = useState([]);
    const [summary, setSummary] = useState({
        total_cars: 0,
        invoiceable_cars: 0,
        discounted_cars: 0,
        raw_balance_total: 0,
        outstanding_balance_total: 0
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [discountLoadingPlate, setDiscountLoadingPlate] = useState('');
    const [discountEditorOpen, setDiscountEditorOpen] = useState(false);
    const [discountCar, setDiscountCar] = useState(null);
    const [discountAmount, setDiscountAmount] = useState('');
    const [defaultMonthlyAmount, setDefaultMonthlyAmount] = useState(0);

    const fetchOwnerCars = useCallback(async () => {
        if (!code) return;

        setLoading(true);
        setError('');

        try {
            const response = await axios.get(`${rootUrl}/owners/${encodeURIComponent(code)}/cars`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });

            if (response.data?.success) {
                setOwner(response.data.owner || null);
                setCars(Array.isArray(response.data.data) ? response.data.data : []);
                setSummary(response.data.summary || {
                    total_cars: 0,
                    invoiceable_cars: 0,
                    discounted_cars: 0,
                    raw_balance_total: 0,
                    outstanding_balance_total: 0
                });
            } else {
                setError(response.data?.message || 'Failed to load owner cars.');
                setCars([]);
            }
        } catch (err) {
            console.error('Error fetching owner cars:', err);
            setError(err.response?.data?.message || 'Failed to load owner cars.');
            setCars([]);
        } finally {
            setLoading(false);
        }
    }, [code, authToken]);

    useEffect(() => {
        fetchOwnerCars();
    }, [fetchOwnerCars]);

    useEffect(() => {
        const fetchDefaultMonthlyAmount = async () => {
            try {
                const response = await axios.get(`${rootUrl}/system/owner_monthly_invoice_amount`, {
                    headers: { Authorization: `Bearer ${authToken}` }
                });
                const amount = Number(response.data?.value || 0);
                setDefaultMonthlyAmount(Number.isFinite(amount) ? amount : 0);
            } catch (err) {
                console.error('Error fetching owner monthly invoice amount:', err);
                setDefaultMonthlyAmount(0);
            }
        };

        fetchDefaultMonthlyAmount();
    }, [authToken]);

    const filteredCars = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        return cars.filter((car) => {
            const matchesSearch =
                !query ||
                String(car.plate_no || '').toLowerCase().includes(query) ||
                String(car.model || '').toLowerCase().includes(query) ||
                String(car.make || '').toLowerCase().includes(query) ||
                String(car.status_name || '').toLowerCase().includes(query);

            const matchesStatus =
                !statusFilter ||
                (statusFilter === 'invoiceable'
                    ? Number(car.status_invoice) === 1 && !hasCarDiscount(car)
                    : statusFilter === 'discounted'
                        ? hasCarDiscount(car)
                        : Number(car.status_invoice) === 0 && !hasCarDiscount(car));

            return matchesSearch && matchesStatus;
        });
    }, [cars, searchTerm, statusFilter]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter, itemsPerPage]);

    const totalPages = Math.max(1, Math.ceil(filteredCars.length / itemsPerPage));
    const paginatedCars = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredCars.slice(start, start + itemsPerPage);
    }, [filteredCars, currentPage, itemsPerPage]);

    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter('');
        setCurrentPage(1);
    };

    const openDiscountEditor = (car) => {
        if (!car?.plate_no) return;

        const existingAmount = Number(car.discount_amount || 0);
        const fallbackAmount = existingAmount > 0 ? existingAmount : defaultMonthlyAmount;
        setDiscountCar(car);
        setDiscountAmount(String(Number.isFinite(fallbackAmount) ? fallbackAmount : 0));
        setDiscountEditorOpen(true);
    };

    const closeDiscountEditor = () => {
        if (discountLoadingPlate) return;
        setDiscountEditorOpen(false);
        setDiscountCar(null);
        setDiscountAmount('');
    };

    const saveDiscount = async () => {
        if (!discountCar?.plate_no) return;

        const amountValue = Math.max(0, Number(discountAmount || 0));
        const fullDiscountThreshold = Number(defaultMonthlyAmount || 0);
        setDiscountLoadingPlate(discountCar.plate_no);
        setError('');

        try {
            await axios.put(
                `${rootUrl}/cars/${encodeURIComponent(discountCar.plate_no)}`,
                {
                    model: discountCar.model,
                    make: discountCar.make,
                    year: discountCar.year,
                    status: discountCar.status,
                    edit_by: user?.username || 'system',
                    drive_link: discountCar.drive_link,
                    owner_code: discountCar.owner_code,
                    is_discounted: fullDiscountThreshold > 0 && amountValue >= fullDiscountThreshold,
                    discount_amount: amountValue
                },
                {
                    headers: { Authorization: `Bearer ${authToken}` }
                }
            );

            await fetchOwnerCars();
            setDiscountEditorOpen(false);
            setDiscountCar(null);
            setDiscountAmount('');
        } catch (err) {
            console.error('Error updating car discount:', err);
            setError(err.response?.data?.message || 'Failed to update car discount.');
        } finally {
            setDiscountLoadingPlate('');
        }
    };

    return (
        <div className="p-6 bg-[#fff8f5] min-h-screen font-sans">
            <div className="mb-8">
                <div className="text-center mb-6">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">
                        {owner?.name || code} Cars
                    </h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80 mt-3">
                        View all cars for this owner and their outstanding balances
                    </p>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
                {error && (
                    <Card className="mb-4 bg-white rounded-xl shadow-lg border border-red-100">
                        <CardBody className="p-4">
                            <Typography className="text-red-600 text-sm">{error}</Typography>
                        </CardBody>
                    </Card>
                )}

                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                    <Card className="bg-white rounded-xl shadow-lg border border-gray-100">
                        <CardBody className="p-4">
                            <Typography variant="small" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Total Cars
                            </Typography>
                            <Typography variant="h4" className="mt-2 font-bold text-[#2a2e30]">
                                {summary.total_cars}
                            </Typography>
                        </CardBody>
                    </Card>
                    <Card className="bg-white rounded-xl shadow-lg border border-gray-100">
                        <CardBody className="p-4">
                            <Typography variant="small" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Invoiceable
                            </Typography>
                            <Typography variant="h4" className="mt-2 font-bold text-[#2a2e30]">
                                {summary.invoiceable_cars}
                            </Typography>
                        </CardBody>
                    </Card>
                    <Card className="bg-white rounded-xl shadow-lg border border-gray-100">
                        <CardBody className="p-4">
                            <Typography variant="small" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Discounted
                            </Typography>
                            <Typography variant="h4" className="mt-2 font-bold text-[#2a2e30]">
                                {summary.discounted_cars}
                            </Typography>
                        </CardBody>
                    </Card>
                    <Card className="bg-white rounded-xl shadow-lg border border-gray-100">
                        <CardBody className="p-4">
                            <Typography variant="small" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Raw Balance
                            </Typography>
                            <Typography variant="h5" className="mt-2 font-bold text-red-600">
                                {formatMoney(summary.raw_balance_total)}
                            </Typography>
                        </CardBody>
                    </Card>
                    <Card className="bg-white rounded-xl shadow-lg border border-gray-100">
                        <CardBody className="p-4">
                            <Typography variant="small" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Outstanding
                            </Typography>
                            <Typography variant="h5" className="mt-2 font-bold text-green-600">
                                {formatMoney(summary.outstanding_balance_total)}
                            </Typography>
                        </CardBody>
                    </Card>
                </div>

                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 sm:gap-4 mb-6 bg-white border border-gray-200 rounded-sm p-3 shadow-none">
                    <div className="flex flex-wrap gap-2 w-full lg:w-auto">
                        <Button
                            onClick={() => setShowFilters(!showFilters)}
                            variant="outlined"
                            size="sm"
                            className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-3 py-2 shadow-none border border-slate-200 normal-case"
                        >
                            <FontAwesomeIcon icon={faFilter} />
                            Filters
                        </Button>
                        <Button
                            onClick={fetchOwnerCars}
                            size="sm"
                            className="flex items-center gap-2 bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-3 py-2 shadow-none normal-case"
                        >
                            <FontAwesomeIcon icon={faSyncAlt} />
                            Refresh
                        </Button>
                    </div>
                    <Typography variant="small" color="blue-gray" className="text-xs font-semibold uppercase tracking-wide">
                        Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredCars.length)}-{Math.min(currentPage * itemsPerPage, filteredCars.length)} of {filteredCars.length} cars
                    </Typography>
                </div>

                {showFilters && (
                    <Card className="mb-6 bg-white rounded-sm border border-gray-200 shadow-none">
                        <CardBody className="p-4 sm:p-5">
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <Input
                                    label="Search"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Plate, make, model, or status"
                                />
                                <Select
                                    label="Billing Type"
                                    value={statusFilter}
                                    onChange={(val) => setStatusFilter(val)}
                                >
                                    <Option value="">All Cars</Option>
                                    <Option value="invoiceable">Billable</Option>
                                    <Option value="discounted">Discounted</Option>
                                    <Option value="non-invoiceable">Non-billable Status</Option>
                                </Select>
                            </div>
                            <div className="mt-4">
                                <Button
                                    size="sm"
                                    variant="text"
                                    onClick={clearFilters}
                                    className="text-slate-700 text-sm rounded-sm px-3 py-2 shadow-none normal-case"
                                >
                                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                                    Clear Filters
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                )}

                {loading ? (
                    <Card className="bg-white rounded-xl shadow-lg border border-gray-100">
                        <CardBody className="p-8 text-center">
                            <Typography>Loading...</Typography>
                        </CardBody>
                    </Card>
                ) : (
                    <Card className="bg-white shadow-lg rounded-xl border border-gray-100 overflow-visible">
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[1024px] table-auto text-left">
                                <thead>
                                    <tr className="border-b border-blue-gray-100 bg-[#EDE4DA]">
                                        <th className="sticky top-0 z-10 rounded-tl-xl bg-[#EDE4DA] p-2 w-[110px]">
                                            <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">Plate</Typography>
                                        </th>
                                        <th className="sticky top-0 z-10 bg-[#EDE4DA] p-2 w-[120px]">
                                            <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">Make</Typography>
                                        </th>
                                        <th className="sticky top-0 z-10 bg-[#EDE4DA] p-2 w-[140px]">
                                            <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">Model</Typography>
                                        </th>
                                        <th className="sticky top-0 z-10 bg-[#EDE4DA] p-2 w-[90px]">
                                            <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">Year</Typography>
                                        </th>
                                        <th className="sticky top-0 z-10 bg-[#EDE4DA] p-2 w-[160px]">
                                            <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">Status</Typography>
                                        </th>
                                        <th className="sticky top-0 z-10 bg-[#EDE4DA] p-2 w-[110px]">
                                            <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">Billing</Typography>
                                        </th>
                                        <th className="sticky top-0 z-10 bg-[#EDE4DA] p-2 w-[130px]">
                                            <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">Added</Typography>
                                        </th>
                                        <th className="sticky top-0 z-10 bg-[#EDE4DA] p-2 w-[140px] text-right">
                                            <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">Outstanding</Typography>
                                        </th>
                                        <th className="sticky top-0 z-10 bg-[#EDE4DA] p-2 w-[140px] text-right">
                                            <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">Raw Balance</Typography>
                                        </th>
                                        <th className="sticky top-0 z-10 bg-[#EDE4DA] p-2 w-[140px]">
                                            <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">Last Transaction</Typography>
                                        </th>
                                        <th className="sticky top-0 z-10 rounded-tr-xl bg-[#EDE4DA] p-2 w-[130px]">
                                            <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">Actions</Typography>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedCars.length === 0 ? (
                                        <tr>
                                            <td colSpan="11" className="p-8 text-center">
                                                No cars found
                                            </td>
                                        </tr>
                                    ) : paginatedCars.map((car) => (
                                        <tr key={car.plate_no} className="border-b border-blue-gray-50 hover:bg-blue-gray-50/50 transition-colors">
                                            <td className="p-2 align-top">
                                                {car.plate_no ? (
                                                    <Link
                                                        to={`/cars/${encodeURIComponent(car.plate_no)}`}
                                                        className="text-xs font-semibold text-[#FF5733] hover:underline"
                                                    >
                                                        {car.plate_no}
                                                    </Link>
                                                ) : (
                                                    <Typography variant="small" className="text-xs font-semibold text-[#2a2e30]">
                                                        -
                                                    </Typography>
                                                )}
                                            </td>
                                            <td className="p-2 align-top">
                                                <Typography variant="small" className="block max-w-[120px] truncate text-xs font-normal">
                                                    {car.make || '-'}
                                                </Typography>
                                            </td>
                                            <td className="p-2 align-top">
                                                <Typography variant="small" className="block max-w-[140px] truncate text-xs font-normal">
                                                    {car.model || '-'}
                                                </Typography>
                                            </td>
                                            <td className="p-2 align-top">
                                                <Typography variant="small" className="text-xs font-normal">
                                                    {car.year || '-'}
                                                </Typography>
                                            </td>
                                            <td className="p-2 align-top">
                                                <span className="inline-flex items-center rounded-full bg-[#EDE4DA] px-2 py-1 text-xs font-semibold text-[#2a2e30]">
                                                    {car.status_name || car.status_code || 'Unknown'}
                                                </span>
                                            </td>
                                            <td className="p-2 align-top">
                                                <Tooltip content={hasCarDiscount(car) ? 'Edit discount amount' : 'Set discount amount'}>
                                                    <Button
                                                        size="sm"
                                                        variant="text"
                                                        onClick={() => openDiscountEditor(car)}
                                                        disabled={discountLoadingPlate === car.plate_no}
                                                        className={`flex h-8 w-8 items-center justify-center rounded-full p-0 shadow-none ${
                                                            hasCarDiscount(car)
                                                                ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                                                                : 'bg-green-50 text-green-700 hover:bg-green-100'
                                                        }`}
                                                        aria-label={hasCarDiscount(car) ? 'Edit discount amount' : 'Set discount amount'}
                                                    >
                                                        <FontAwesomeIcon icon={faTag} className="h-3.5 w-3.5" />
                                                    </Button>
                                                </Tooltip>
                                            </td>
                                            <td className="p-2 align-top">
                                                <Typography variant="small" className="text-xs font-normal text-slate-700">
                                                    {getFirstAddedDate(car)}
                                                </Typography>
                                            </td>
                                            <td className="p-2 align-top text-right">
                                                <Typography variant="small" className={`text-xs font-semibold ${Number(car.actual_bal) >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                    {formatMoney(car.actual_bal)}
                                                </Typography>
                                            </td>
                                            <td className="p-2 align-top text-right">
                                                <Typography variant="small" className={`text-xs font-semibold ${Number(car.bal) >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                    {formatMoney(car.bal)}
                                                </Typography>
                                            </td>
                                            <td className="p-2 align-top">
                                                <Typography variant="small" className="text-xs font-normal">
                                                    {car.last_trans_type || '-'}
                                                </Typography>
                                                <Typography variant="small" color="gray" className="text-xs">
                                                    {formatDate(car.last_trans_date)}
                                                </Typography>
                                            </td>
                                            <td className="p-2 align-top">
                                                <div className="flex flex-wrap gap-2">
                                                    <Tooltip content="Open car page">
                                                        <Button
                                                            size="sm"
                                                            onClick={() => navigate(`/cars/${encodeURIComponent(car.plate_no)}`)}
                                                            className="w-8 h-8 p-2 rounded-full border border-slate-200 bg-slate-100 text-slate-700 shadow-none hover:bg-slate-200"
                                                            aria-label="Open car page"
                                                        >
                                                            <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
                                                        </Button>
                                                    </Tooltip>
                                                    <Tooltip content="Owner statement">
                                                        <Button
                                                            size="sm"
                                                            variant="outlined"
                                                            onClick={() => navigate(`/owners/statements/${encodeURIComponent(code)}`)}
                                                            className="w-8 h-8 p-2 rounded-full border border-slate-200 bg-slate-100 text-slate-700 shadow-none hover:bg-slate-200"
                                                            aria-label="Open owner statement"
                                                        >
                                                            <FontAwesomeIcon icon={faFileInvoice} className="h-3 w-3" />
                                                        </Button>
                                                    </Tooltip>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {filteredCars.length > 0 && (
                            <div className="flex flex-col gap-4 border-t border-blue-gray-100 p-4 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-center gap-3">
                                    <Typography variant="small" color="blue-gray" className="text-xs font-medium">
                                        Per page:
                                    </Typography>
                                    <select
                                        value={itemsPerPage}
                                        onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                        className="h-9 rounded-sm border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:border-[#FF5733] focus:outline-none focus:ring-1 focus:ring-[#FF5733]"
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                    </select>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Button
                                        size="sm"
                                        variant="outlined"
                                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className="flex items-center gap-1 rounded-sm px-3 py-2 shadow-none normal-case"
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3" />
                                        Prev
                                    </Button>

                                    <Typography variant="small" color="blue-gray" className="px-2 text-sm">
                                        Page {currentPage} of {totalPages}
                                    </Typography>

                                    <Button
                                        size="sm"
                                        variant="outlined"
                                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage >= totalPages}
                                        className="flex items-center gap-1 rounded-sm px-3 py-2 shadow-none normal-case"
                                    >
                                        Next
                                        <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Card>
                )}

                {discountEditorOpen && discountCar && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
                        <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
                            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                                <div>
                                    <Typography variant="h6" className="text-[#2a2e30]">
                                        Set Discount Amount
                                    </Typography>
                                    <Typography variant="small" className="text-gray-500">
                                        {discountCar.plate_no} - {discountCar.model || discountCar.make || 'Car'}
                                    </Typography>
                                </div>
                                <button
                                    type="button"
                                    onClick={closeDiscountEditor}
                                    className="rounded-full p-2 text-slate-500 hover:bg-slate-100"
                                    aria-label="Close discount editor"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>

                            <div className="space-y-4 px-5 py-5">
                                <div>
                                    <label className="mb-2 block text-sm font-medium text-slate-700">
                                        Discount Amount
                                    </label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={discountAmount}
                                        onChange={(e) => setDiscountAmount(e.target.value)}
                                        label="Discount Amount"
                                    />
                                    <p className="mt-2 text-xs text-slate-500">
                                        Defaults to the monthly invoice amount so the car is fully discounted.
                                    </p>
                                </div>

                                <div className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900">
                                    Current discount: {formatMoney(discountCar.discount_amount || 0)}
                                    <div className="mt-1 text-xs text-amber-800">
                                        Default monthly amount: {formatMoney(defaultMonthlyAmount)}
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-5 py-4">
                                <Button
                                    variant="outlined"
                                    onClick={closeDiscountEditor}
                                    className="rounded-sm shadow-none normal-case"
                                    disabled={Boolean(discountLoadingPlate)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={saveDiscount}
                                    disabled={Boolean(discountLoadingPlate)}
                                    className="rounded-sm bg-[#FF5733] text-white shadow-none normal-case"
                                >
                                    {discountLoadingPlate === discountCar.plate_no ? 'Saving...' : 'Save'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OwnerCars;
